// PARAQTA — Public Profile (Stage 6.9.4).
// URL: /u/{slug} — custom_slug немесе Telegram username.
// Brand: gold "PARAQTA-ның Жан досы" badge (Premium only).

import { show } from '../../core/router.js';
import { esc } from '../../utils/format.js';
import { toast } from '../../utils/toast.js';
import { showLoginModal } from '../auth/login-modal.js';

const MONTHS_SHORT_KK = ['қаң', 'ақп', 'нау', 'сәу', 'мам', 'мау',
                         'шіл', 'там', 'қыр', 'қаз', 'қар', 'жел'];

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return `${d.getDate()} ${MONTHS_SHORT_KK[d.getMonth()]}`;
}

function topNav(shareUrl) {
  return `
    <nav class="v2-top-nav">
      <button class="v2-nav-btn" data-action="pp-back" aria-label="Артқа">
        <i class="ti ti-arrow-left"></i>
      </button>
      <div class="v2-nav-title">Профиль</div>
      <button class="v2-nav-btn" data-action="pp-share" data-url="${esc(shareUrl)}" aria-label="Бөлісу">
        <i class="ti ti-share"></i>
      </button>
    </nav>
  `;
}

function renderHeatmap(days) {
  if (!days || !days.length) return '';
  const maxCount = days.reduce((m, d) => Math.max(m, d.count), 0) || 1;
  const cells = days.map(d => {
    const c = d.count;
    let lvl = 0;
    if (c > 0) {
      const r = c / maxCount;
      lvl = r < 0.25 ? 1 : r < 0.5 ? 2 : r < 0.75 ? 3 : 4;
    }
    return `<div class="pp-hm-cell pp-hm-l${lvl}" title="${esc(d.date)} — ${c}"></div>`;
  }).join('');
  return `<div class="pp-hm-grid">${cells}</div>`;
}

function renderProfile(p) {
  const initials = (p.display_name || '?').slice(0, 2).toUpperCase();
  const handle = p.custom_slug || p.username || String(p.telegram_id);
  const shareUrl = `https://paraqta.kz/u/${handle}`;
  const stars = (n) => '★'.repeat(Math.max(0, Math.min(5, n || 0))) +
                       '☆'.repeat(Math.max(0, 5 - (n || 0)));
  const cb = p.current_book;
  const cbPct = cb && cb.pages
    ? Math.max(0, Math.min(100, ((cb.current_page || 0) / cb.pages) * 100))
    : 0;

  return `
${topNav(shareUrl)}

<div class="pp-page">

  <!-- HERO -->
  <section class="pp-hero">
    <div class="pp-avatar-wrap">
      <div class="pp-avatar">${esc(initials)}</div>
      ${p.is_jandos ? `<div class="pp-feather-badge"><i class="ti ti-feather"></i></div>` : ''}
    </div>

    <h1 class="pp-name">${esc(p.display_name)}</h1>

    ${p.is_jandos ? `
      <div class="pp-jandos-badge">
        <i class="ti ti-feather"></i>
        PARAQTA-ның Жан досы
      </div>
    ` : ''}

    <div class="pp-meta">
      ${p.city ? esc(p.city) + ' · ' : ''}${p.stats.streak} күн қатарынан оқиды
    </div>
  </section>

  <!-- STATS -->
  <section class="pp-stats">
    <div class="pp-stat">
      <div class="pp-stat-num">${p.stats.books}</div>
      <div class="pp-stat-label">кітап</div>
    </div>
    <div class="pp-stat">
      <div class="pp-stat-num">${p.stats.streak} <span class="pp-fire">🔥</span></div>
      <div class="pp-stat-label">streak</div>
    </div>
    <div class="pp-stat">
      <div class="pp-stat-num">${p.stats.followers}</div>
      <div class="pp-stat-label">followers</div>
    </div>
  </section>

  <!-- ACTIONS (Stage 7.7: нақты follow) -->
  ${p.is_self ? '' : `
  <section class="pp-actions">
    <button class="pp-cta-primary" data-action="pp-follow" data-user-id="${p.telegram_id}" data-following="${p.is_following ? '1' : '0'}">
      <i class="ti ti-${p.is_following ? 'user-check' : 'user-plus'}"></i>
      ${p.is_following ? 'Қадағалаудамын' : 'Қадағалау'}
    </button>
  </section>
  `}

  <!-- ҚАЗІР ОҚЫП ЖАТЫР -->
  ${cb ? `
  <section class="pp-section">
    <div class="pp-eyebrow">ҚАЗІР ОҚЫП ЖАТЫР</div>
    <div class="pp-card pp-current-book">
      <div class="pp-cb-cover">
        <div class="pp-cb-cover-text">${esc(cb.title || '')}</div>
      </div>
      <div class="pp-cb-info">
        <div class="pp-cb-title">${esc(cb.title || '')}</div>
        <div class="pp-cb-author">${esc(cb.author || '')}</div>
        ${cb.pages ? `
          <div class="pp-cb-bar"><div class="pp-cb-bar-fill" style="width:${cbPct}%"></div></div>
          <div class="pp-cb-pages">${cb.current_page || 0} / ${cb.pages} бет</div>
        ` : ''}
      </div>
    </div>
  </section>
  ` : ''}

  <!-- СОҢҒЫ РЕЦЕНЗИЯЛАР -->
  ${(p.recent_reviews && p.recent_reviews.length) ? `
  <section class="pp-section">
    <div class="pp-eyebrow">СОҢҒЫ РЕЦЕНЗИЯЛАР</div>
    <div class="pp-reviews">
      ${p.recent_reviews.map(r => `
        <div class="pp-review">
          <div class="pp-review-book">${esc(r.book_title || 'Кітап')}</div>
          ${r.rating ? `<div class="pp-review-stars">${stars(r.rating)}</div>` : ''}
          <p class="pp-review-text">«${esc((r.text || '').slice(0, 140))}${(r.text || '').length > 140 ? '…' : ''}»</p>
          <div class="pp-review-date">${esc(fmtDate(r.created_at))}</div>
        </div>
      `).join('')}
    </div>
  </section>
  ` : ''}

  <!-- STREAK HEATMAP (Жан дос only) -->
  ${(p.is_jandos && p.heatmap) ? `
  <section class="pp-section">
    <div class="pp-eyebrow">STREAK — ${p.stats.streak} КҮН</div>
    <div class="pp-heatmap">${renderHeatmap(p.heatmap)}</div>
  </section>
  ` : ''}

</div>
  `;
}

function renderNotFound(slug) {
  const handle = slug || '?';
  return `
    ${topNav('https://paraqta.kz/')}
    <div class="pp-empty">
      <i class="ti ti-user-question"></i>
      <h2>Табылмады</h2>
      <p><em>@${esc(handle)}</em> деген қолданушы PARAQTA-да жоқ.</p>
    </div>
  `;
}

export async function loadPublicProfile(slug) {
  const root = document.getElementById('pg-public-profile');
  if (!root) return;
  if (!slug && window._pendingPublicProfile) {
    slug = window._pendingPublicProfile;
    window._pendingPublicProfile = null;
  }
  if (!slug) {
    const m = window.location.pathname.match(/^\/u\/([^/]+)/);
    slug = m ? decodeURIComponent(m[1]) : null;
  }
  if (!slug) {
    root.innerHTML = renderNotFound('');
    return;
  }

  root.innerHTML = topNav('https://paraqta.kz/') +
    '<div class="pp-loading">Жүктелуде…</div>';

  try {
    const r = await fetch('/api/u/' + encodeURIComponent(slug));
    const data = await r.json();
    if (!data || data.status !== 'ok' || !data.profile) {
      root.innerHTML = renderNotFound(slug);
      return;
    }
    root.innerHTML = renderProfile(data.profile);
  } catch (e) {
    console.error('public profile load failed', e);
    root.innerHTML = topNav('https://paraqta.kz/') +
      '<div class="pp-error">Қате — қайта көрейік.</div>';
  }
}

export function publicProfileBack() {
  // Path-based route — go back to home
  if (window.history.length > 1) {
    window.history.back();
  } else {
    window.location.href = '/';
  }
}

export async function ppFollow(btn) {
  const id = btn?.dataset?.userId;
  if (!id) return;
  const following = btn.dataset.following === '1';
  const url = (following ? '/api/unfollow/' : '/api/follow/') + encodeURIComponent(id);
  btn.disabled = true;
  try {
    const r = await fetch(url, { method: 'POST', credentials: 'include' });
    if (r.status === 401) {
      toast('Алдымен кіріңіз');
      try { showLoginModal(); } catch (_) {}
      return;
    }
    if (!r.ok) { toast('Қате болды'); return; }
    const now = !following;
    btn.dataset.following = now ? '1' : '0';
    btn.innerHTML = `<i class="ti ti-${now ? 'user-check' : 'user-plus'}"></i> ${now ? 'Қадағалаудамын' : 'Қадағалау'}`;
    toast(now ? 'Қадағалап жатырсың ✓' : 'Қадағалаудан шықтың');
  } catch (e) {
    toast('Қате болды');
  } finally {
    btn.disabled = false;
  }
}

export async function shareProfile(url) {
  if (!url) return;
  if (navigator.share) {
    try { await navigator.share({ title: 'PARAQTA — Оқырман профилі', url }); }
    catch (_) { /* user cancelled */ }
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    toast('Сілтеме көшірілді');
  } catch (_) {
    toast(url);
  }
}
